import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // 🎯 استدعاء الترجمة
import { usePermissions } from './usePermissions';
import { useFontSize } from './FontSizeContext';
import { exportToExcel } from './exportUtils';

import { 
  Plus, Search, ChevronLeft, ChevronRight, 
  ArrowUpDown, Tag, Package, Box, Filter, X, RefreshCw  
} from 'lucide-react';
import toast from 'react-hot-toast';

const ItemList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  // 🎯 إعدادات الترجمة والاتجاه
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  const { canCreate } = usePermissions('items');

  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  // فلاتر صفية (Filter Row)
  const [filters, setFilters] = useState({ itemnum: "", description: "", itemtype: "", status: "" });
  const [sortConfig, setSortConfig] = useState({ key: 'itemnum', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  const [totalItems, setTotalItems] = useState(0);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/items', {
        params: {
          page: currentPage,
          limit: itemsPerPage,
          sort_by: sortConfig.key,
          sort_order: sortConfig.direction,
          itemnum: filters.itemnum,
          description: filters.description,
          status: filters.status,
          itemtype: filters.itemtype
        }
      });
      setItems(response.data.items);
      setTotalItems(response.data.total);
    } catch (err) {
      toast.error(t('itemList.messages.syncFailed'));
    } finally {
      setLoading(false);
    }
  };

  // 🎯 السحر هنا: إعادة التحميل عند أي تغيير
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchItems();
    }, 300); // Debounce عشان ما يبعتش طلب مع كل حرف في البحث
    return () => clearTimeout(delayDebounceFn);
  }, [currentPage, filters, sortConfig, t]);

  // دالة الترتيب عند الضغط على الهيدر
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handleExport = useCallback(() => {

    // تجميع كل الفلاتر النشطة (الحقول + البحث السريع + فيو السايدبار)
    let activeFilters = {
        ...filters,
    };


    // تنظيف الفلاتر الفاضية عشان متبعتش للباك إند
    activeFilters = Object.fromEntries(
        Object.entries(activeFilters).filter(([_, value]) => value !== '' && value !== null)
    );

    const columnsMap = {
        "itemnum": t('itemList.tables.item', 'Item Num'),
        "description": t('assets.headers.desc', 'Description'),
        "status": t('wo.headers.status', 'Status'),
        "itemtype": t('type', 'Type')
    };

    exportToExcel('items', activeFilters, columnsMap, t);
  }, [filters, t]);

  useEffect(() => {
    const exportData = () => { handleExport(); };
    window.addEventListener('GLOBAL_EXPORT', exportData);
    return () => window.removeEventListener('GLOBAL_EXPORT', exportData);
  }, [handleExport]);

  const filteredData = useMemo(() => {
    return items.filter(item => 
      (item.itemnum?.toLowerCase() || "").includes(filters.itemnum.toLowerCase()) &&
      (item.description?.toLowerCase() || "").includes(filters.description.toLowerCase()) &&
      (item.itemtype?.toLowerCase() || "").includes(filters.itemtype.toLowerCase()) &&
      (item.status?.toLowerCase() || "").includes(filters.status.toLowerCase())
    );
  }, [items, filters]);

  const currentData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div 
        className={`flex flex-col h-full bg-[#f3f4f6] text-[11px] uppercase ${isRTL ? 'font-cairo dir-rtl text-right' : 'font-sans text-left'}`} 
        style={{ fontSize: `${fontSize}px` }}
        dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Toolbar */}
      <div className="bg-white p-3 border-b shadow-sm flex justify-between items-center px-6 shrink-0">
        <div className={`flex items-center gap-6 `}>
          <h2 className={`text-[#1a3353] font-black italic text-lg flex items-center gap-3 `}>
            <Tag size={22} className="text-blue-600" /> {t('itemList.headers.master')}
          </h2>
          <button 
            onClick={fetchItems} 
            className={`p-2 rounded-full transition-all hover:bg-gray-100 ${loading ? 'animate-spin text-blue-600' : 'text-gray-400'}`}
            title={t('common.refresh')}
          >
            <RefreshCw size={18} />
          </button>
        </div>
        {canCreate && (
        <button onClick={() => navigate('/items/new')} className={`bg-[#3c70a4] text-white px-6 py-2 rounded-sm font-black flex items-center gap-2 hover:bg-[#1a3353] transition-all shadow-md active:scale-95 `}>
          <Plus size={16} /> {t('itemList.actions.newItem')}
        </button>
        )}
      </div>

      {/* Table Container */}
      <div className="flex-1 overflow-auto p-4 custom-scrollbar">
        <div className="bg-white border shadow-sm rounded-sm overflow-hidden">
          <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
            <thead className="bg-gray-50 border-b text-gray-700 font-black text-[0.9] sticky top-0 z-20 tracking-widest">
              <tr className={isRTL ? 'flex-row-reverse text-right' : 'text-left'}>
                <th className={`p-3 border-x cursor-pointer hover:bg-gray-200 transition-colors w-48 ${isRTL ? 'border-l' : 'border-r'}`} onClick={() => handleSort('itemnum')}>
                    <div className={`flex items-center justify-between `}>
                        {t('itemList.tables.item')} 
                        <ArrowUpDown size={12} className={sortConfig.key === 'itemnum' ? 'text-blue-600' : 'text-gray-400'}/>
                    </div>
                </th>
                <th className={`p-3 border-x cursor-pointer hover:bg-gray-200 transition-colors ${isRTL ? 'border-l' : 'border-r'}`} onClick={() => handleSort('description')}>
                    <div className={`flex items-center justify-between `}>
                        {t('assets.headers.desc')} 
                        <ArrowUpDown size={12} className={sortConfig.key === 'description' ? 'text-blue-600' : 'text-gray-400'}/>
                    </div>
                </th>
                <th className={`p-3 border-x w-32 text-center cursor-pointer hover:bg-gray-200 transition-colors ${isRTL ? 'border-l' : 'border-r'}`} onClick={() => handleSort('itemtype')}>
                    <div className="flex items-center justify-center gap-2">
                        {t('type')} <ArrowUpDown size={12} className={sortConfig.key === 'itemtype' ? 'text-blue-600' : 'text-gray-400'}/>
                    </div>
                </th>
                <th className={`p-3 border-x w-32 text-center cursor-pointer hover:bg-gray-200 transition-colors ${isRTL ? 'border-l' : 'border-r'}`} onClick={() => handleSort('status')}>
                    <div className="flex items-center justify-center gap-2">
                        {t('assets.headers.status')} <ArrowUpDown size={12} className={sortConfig.key === 'status' ? 'text-blue-600' : 'text-gray-400'}/>
                    </div>
                </th>
                <th className="p-3 w-28 text-center">{t('itemList.tables.rotating')}</th>
              </tr>
              {/* Filter Row */}
              <tr className={`bg-[#f1f5f9] shadow-inner `}>
                <td className="p-1 border-x border-gray-200">
                    <input name="itemnum" placeholder={t('common.filter')} value={filters.itemnum} onChange={handleFilterChange} className={`w-full border p-1.5 outline-none font-bold text-blue-900 uppercase focus:border-blue-400 ${isRTL ? 'text-right' : 'text-left'}`} />
                </td>
                <td className="p-1 border-x border-gray-200">
                    <input name="description" placeholder={t('common.filter')} value={filters.description} onChange={handleFilterChange} className={`w-full border p-1.5 outline-none font-bold text-blue-900 focus:border-blue-400 ${isRTL ? 'text-right' : 'text-left'}`} />
                </td>
                <td className="p-1 border-x border-gray-200">
                    <input name="itemtype" placeholder={t('common.filter')} value={filters.itemtype} onChange={handleFilterChange} className="w-full border p-1.5 outline-none font-bold text-blue-900 text-center focus:border-blue-400" />
                </td>
                <td className="p-1 border-x border-gray-200">
                    <input name="status" placeholder={t('common.filter')} value={filters.status} onChange={handleFilterChange} className="w-full border p-1.5 outline-none font-bold text-blue-900 text-center focus:border-blue-400" />
                </td>
                <td className="bg-gray-200/50"></td>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                  <tr><td colSpan="5" className="p-20 text-center font-black text-gray-300 animate-pulse tracking-[0.2em]">{t('itemList.messages.syncing')}</td></tr>
              ) : currentData.length > 0 ? (
                  currentData.map((item) => (
                    <tr key={item.itemnum} onClick={() => navigate(`/items/${item.itemnum}`)} className={`border-b hover:bg-blue-50 cursor-pointer group transition-colors ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}>
                      <td className={`p-3 border-x font-black text-blue-900 group-hover:underline underline-offset-4 ${isRTL ? 'border-l' : 'border-r'}`}>{item.itemnum}</td>
                      <td className={`p-3 border-x text-gray-600 font-bold ${isRTL ? 'border-l' : 'border-r'}`}>{item.description}</td>
                      <td className={`p-3 border-x text-center font-black text-gray-500 ${isRTL ? 'border-l' : 'border-r'}`}>{item.itemtype}</td>
                      <td className={`p-3 border-x text-center ${isRTL ? 'border-l' : 'border-r'}`}>
                        <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-sm text-[0.8] font-black shadow-sm border border-blue-200">{item.status}</span>
                      </td>
                      <td className="p-3 text-center font-black text-slate-500">
                          {item.rotating ? <span className="text-green-600 bg-green-50 px-2 py-0.5 rounded border border-green-200">{t('common.yes')}</span> : <span className="text-gray-400">{t('common.no')}</span>}
                      </td>
                    </tr>
                  ))
              ) : (
                  <tr><td colSpan="5" className="p-20 text-center font-black text-gray-400 italic uppercase">{t('itemList.messages.noData')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Pagination Footer */}
      <div className={`bg-white p-3 border-t flex justify-between items-center font-black text-[#1a3353] shrink-0 `}>
        <div className={`flex items-center gap-2 `}>
            <span className="text-gray-400">{t('itemList.footer.totalItems')}</span> 
            <span className="text-lg text-blue-600">{totalItems}</span>
        </div>
        
        <div className={`flex items-center gap-4 `}>
            <button 
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
                className="p-1 border rounded disabled:opacity-30 hover:bg-gray-100 transition-colors active:scale-90"
            >
                <ChevronLeft size={18} className={isRTL ? 'rotate-180' : ''} />
            </button>
            
            <span className="bg-blue-900 text-white px-4 py-1.5 rounded-sm shadow-inner tracking-widest">
                {t('itemList.footer.page')} {currentPage}
            </span>
            
            <button 
                disabled={currentData.length < itemsPerPage || (currentPage * itemsPerPage) >= totalItems}
                onClick={() => setCurrentPage(prev => prev + 1)}
                className="p-1 border rounded disabled:opacity-30 hover:bg-gray-100 transition-colors active:scale-90"
            >
                <ChevronRight size={18} className={isRTL ? 'rotate-180' : ''} />
            </button>
        </div>
      </div>
    </div>
  );
};

export default ItemList;